import type { CategoryItem } from "../types/category-panel";

/**
 * Caminho (raiz → atual) na árvore de categorias a partir de uma pilha de ids
 * do `useViewStack`, andando um nível por id. Ids que não casam com nenhum
 * item do nível interrompem a busca (pilha obsoleta, ex.: dados recarregados).
 *
 * Usado pelos dois drill-downs — o fullscreen do mobile (CategoryPanelMobile)
 * e o de dentro da coluna no desktop (CategoryDetailCard) — para suportar
 * profundidade arbitrária com a mesma regra.
 */
export function resolveCategoryPath(roots: CategoryItem[], stack: string[]): CategoryItem[] {
    const path: CategoryItem[] = [];
    let level = roots;

    for (const id of stack) {
        const found = level.find((cat) => cat.id === id);
        if (!found) break;
        path.push(found);
        level = found.children ?? [];
    }

    return path;
}

/**
 * Ids da raiz até a categoria — a pilha que o drill-down precisa para abrir já
 * nela (ex.: aba da barra que é uma subcategoria). Vazio quando ela não está na árvore.
 */
export function findCategoryPath(roots: CategoryItem[], id: string): string[] {
    for (const cat of roots) {
        if (cat.id === id) return [cat.id];
        const rest = cat.children ? findCategoryPath(cat.children, id) : [];
        if (rest.length > 0) return [cat.id, ...rest];
    }
    return [];
}

/** Último item do caminho — a categoria em que o drill-down está parado. */
export function lastOf(path: CategoryItem[]): CategoryItem | undefined {
    return path[path.length - 1];
}
